/**
 * Empty rows, and the sentence each one owes the reader (DOP-293).
 *
 * A metric that produced no number used to vanish from the grid. That is the
 * one thing a dashboard built on "every number is traceable" must not do: a row
 * that is not there cannot be told apart from a row that broke, so the user's
 * question stops being "what does this say" and becomes "is this thing
 * working". The grid now carries those rows, hidden behind a filter that is ON
 * by default — today's view unchanged — and every one of them explains itself
 * when shown.
 *
 * The WORDING lives here rather than on the server. The server sends a stable
 * token (`empty_reason.kind`) plus its own detail prose; the sentence a user
 * reads is a product decision that changes more often than a contract should,
 * and the planner's internal vocabulary ("near_miss", "no_source", "mapping
 * confidence threshold") must never reach a screen. That is why the detail
 * prose is NOT shown: it is written in the planner's terms, and appended to
 * the sentence it only said the same thing twice in words a customer could
 * not read.
 */

import {
	type KpiEmptyReason,
	type KpiMetricGroup,
	type KpiMetricRow,
	type KpiTable,
	isCustomMetricRow,
} from "../../types/kpi";

/**
 * Does this row hold no number at all?
 *
 * Cells the server omitted and cells it sent with a null `effective_value` are
 * the same fact to a reader — an empty column — so both count. A row is empty
 * only when EVERY column is.
 */
export function isEmptyMetricRow(metric: KpiMetricRow): boolean {
	return Object.values(metric.cells).every(
		(cell) => cell.effective_value === null || cell.effective_value === undefined,
	);
}

/**
 * Join the missing input labels the way a person would say them.
 *
 * "Total Rent Charged and Total Units", not "Total Rent Charged, Total Units" —
 * the line is read as a sentence, and this is the half of it that names things
 * the user recognises from the rows above.
 */
function listLabels(labels: string[]): string {
	if (labels.length === 0) return "";
	if (labels.length === 1) return labels[0];
	return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/**
 * The one line every empty row shows on the grid.
 *
 * The specific reasons below turned out to be more than a customer wanted to
 * read on every blank row, so the grid says this and nothing else. The specific
 * sentence is still worked out per row and written to the browser console (see
 * `emptyRowReasons`), which is where someone supporting the customer reads it.
 */
export const EMPTY_ROW_MESSAGE = "No value found for this metric.";

/**
 * One sentence per reason kind. Four extraction verdicts, four sentences —
 * because they are four different facts about the documents.
 *
 * `out_of_window` used to share "not stated in these documents" with
 * `no_value`, and that was a false claim in the one case users actually hit: a
 * label DOES map to the metric and the documents DO state it, outside the date
 * range the workflow asked for. Someone reading 2026 exports through a
 * 2025-window run was told the number was absent while it sat on the page in
 * front of them. The sentence now points at the date range, which is the thing
 * they can change.
 *
 * `filled_elsewhere` is not an absence at all — the run has the number, just
 * not for this property or these periods — so it must never send the reader
 * back to the documents.
 *
 * There is deliberately NO entry for the retired `not_stated` kind. A server
 * rolled back far enough to emit it falls through to "No value found for this
 * metric.", which is weak but true; re-adding the old sentence would reinstate
 * the overclaim on exactly the path nobody would be watching.
 */
const KIND_SENTENCE: Record<string, string> = {
	out_of_window:
		"Your documents have this metric, but only for dates outside this workflow's date range.",
	no_value:
		"We checked your documents and they don't give a value for this metric.",
	filled_elsewhere:
		"We have this metric, but not for this property or these periods.",
	near_match:
		"We found a similar line in your documents but weren't sure it is this metric, so we left it blank.",
	unmapped: "We couldn't find this metric in your documents.",
};

/**
 * The specific explanation for one empty row. Logged, not shown — the grid
 * shows `EMPTY_ROW_MESSAGE`.
 *
 * Never returns an empty string: a blank with nothing beside it is exactly the
 * state this ticket exists to remove, so a row whose reason the server did not
 * record still says that no value was found — an honest "we have nothing on
 * this" rather than a silence the user has to interpret.
 */
export function emptyRowExplanation(
	reason: KpiEmptyReason | null | undefined,
): string {
	if (!reason) return "No value found for this metric.";

	if (reason.kind === "missing_inputs") {
		const names = listLabels(reason.missing_inputs);
		// A calculated row with no named inputs would produce "Needs  — not
		// available", so fall through to the generic line instead.
		return names
			? `Needs ${names} — not available.`
			: "Calculated from values this grid does not have.";
	}

	// `reason.detail` is deliberately not appended — see the note at the top.
	return KIND_SENTENCE[reason.kind] ?? "No value found for this metric.";
}


/**
 * The groups with every all-blank row removed, and with them any group left
 * holding nothing.
 *
 * A section heading over no rows reads as a rendering fault — the impression
 * this ticket exists to remove, not to relocate.
 *
 * USER-DEFINED rows are exempt. The filter hides rows the extraction did not
 * fill; a custom row is on the grid because this user put it there, and its row
 * carries the only edit and delete controls it has. Hiding a formula that
 * currently computes to nothing would take away the handle for fixing the
 * formula — leaving the user with a row they cannot see and cannot remove.
 */
export function metricGroupsWithoutEmptyRows(
	groups: KpiMetricGroup[],
): KpiMetricGroup[] {
	return groups
		.map((group) => ({
			...group,
			metrics: group.metrics.filter(
				(metric) =>
					isCustomMetricRow(metric.key, group.key) ||
					!isEmptyMetricRow(metric),
			),
		}))
		.filter((group) => group.metrics.length > 0);
}

/**
 * Every empty row with its specific reason, in grid order — what the console
 * log carries in place of the sentence the grid no longer shows. `kind` is the
 * server's token, there so a log line can be matched to the run's own record.
 */
export function emptyRowReasons(
	groups: KpiMetricGroup[],
): { metric: string; kind: string; reason: string }[] {
	return groups.flatMap((group) =>
		group.metrics.filter(isEmptyMetricRow).map((metric) => ({
			metric: metric.label,
			kind: metric.empty_reason?.kind ?? "none",
			reason: emptyRowExplanation(metric.empty_reason),
		})),
	);
}

/**
 * The labels of the rows `metricGroupsWithoutEmptyRows` drops, in grid order —
 * how many rows the Empty rows switch acts on. Custom rows are exempt here exactly as they are there.
 */
export function hideableEmptyRowLabels(groups: KpiMetricGroup[]): string[] {
	return groups.flatMap((group) =>
		group.metrics
			.filter(
				(metric) =>
					!isCustomMetricRow(metric.key, group.key) &&
					isEmptyMetricRow(metric),
			)
			.map((metric) => metric.label),
	);
}

/**
 * The same filter applied to a whole table.
 *
 * Used by the surfaces that consume the table rather than render the grid — the
 * spreadsheet export and the trend charts. They have to agree with what is on
 * screen: a download that carries rows the user chose to hide, or a chart panel
 * with an empty card per unfilled metric, would make the toggle a lie about
 * everything except the grid.
 */
export function tableWithoutEmptyRows(table: KpiTable): KpiTable {
	return { ...table, groups: metricGroupsWithoutEmptyRows(table.groups) };
}
